/**
 * Projections Analysis HTML Export Utility
 * 
 * Converts projections analysis backtest data into a downloadable HTML file with inline styling.
 * The HTML file includes all accuracy metrics, historical enrollment tables, and backtest scenarios.
 */

/**
 * Export projections analysis to HTML file
 * @param {string} schoolName - Name of the school
 * @param {string} teamName - Name of the team
 * @param {Object} analysis - Analysis data from getSchoolDetailedAnalysis
 * @param {Object} chartImages - Optional chart images as base64 data URLs
 */
export const exportProjectionsAnalysisToHTML = (
  schoolName,
  teamName,
  analysis,
  chartImages = {}
) => {
  const timestamp = new Date().toLocaleString();
  const sanitizedSchoolName = schoolName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  const filename = `${sanitizedSchoolName}_projections_analysis_${Date.now()}.html`;

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Projections Analysis - ${schoolName}</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background-color: #f9fafb;
            padding: 40px 20px;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            background-color: white;
            padding: 40px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .header {
            border-bottom: 3px solid #4338ca;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        
        h1 {
            color: #4338ca;
            font-size: 32px;
            margin-bottom: 10px;
        }
        
        .subtitle {
            color: #6b7280;
            font-size: 14px;
        }
        
        .team-badge {
            display: inline-block;
            background-color: #e0e7ff;
            color: #4338ca;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 14px;
            margin-top: 10px;
        }
        
        .section {
            margin-bottom: 40px;
            padding: 20px;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
        }
        
        .section-header {
            display: flex;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #e5e7eb;
        }
        
        .section-icon {
            width: 40px;
            height: 40px;
            background-color: #4338ca;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            font-weight: bold;
            font-size: 18px;
        }
        
        .section-icon svg {
            width: 24px;
            height: 24px;
            fill: white;
        }
        
        h2 {
            color: #111827;
            font-size: 24px;
        }
        
        h3 {
            color: #374151;
            font-size: 18px;
            margin: 20px 0 10px 0;
        }
        
        .metric-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;
            margin: 20px 0;
        }
        
        .metric-card {
            background-color: #f9fafb;
            padding: 20px;
            border-radius: 6px;
            text-align: center;
            border: 1px solid #e5e7eb;
        }
        
        .metric-value {
            font-size: 32px;
            font-weight: bold;
            color: #4338ca;
            margin: 10px 0;
        }
        
        .metric-value.success {
            color: #10b981;
        }
        
        .metric-value.warning {
            color: #f59e0b;
        }
        
        .metric-value.error {
            color: #ef4444;
        }
        
        .metric-label {
            font-size: 14px;
            color: #6b7280;
        }
        
        .metric-sublabel {
            font-size: 12px;
            color: #9ca3af;
            margin-top: 4px;
        }
        
        table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 14px;
        }
        
        th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e5e7eb;
        }
        
        th {
            background-color: #f9fafb;
            font-weight: 600;
            color: #374151;
        }
        
        tr:hover {
            background-color: #f9fafb;
        }
        
        .badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 500;
        }
        
        .badge-success {
            background-color: #d1fae5;
            color: #065f46;
        }
        
        .badge-warning {
            background-color: #fef3c7;
            color: #92400e;
        }
        
        .badge-error {
            background-color: #fee2e2;
            color: #991b1b;
        }
        
        .badge-info {
            background-color: #dbeafe;
            color: #1e40af;
        }
        
        .positive {
            color: #10b981;
        }
        
        .negative {
            color: #ef4444;
        }
        
        .enrollment-cell {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            min-width: 40px;
            text-align: center;
        }
        
        .enrollment-active {
            background-color: #d1fae5;
            color: #065f46;
        }
        
        .enrollment-empty {
            background-color: #f3f4f6;
            color: #9ca3af;
        }
        
        .scenario-card {
            background-color: #f8fafc;
            padding: 20px;
            border-radius: 8px;
            border-left: 4px solid #4338ca;
            margin-bottom: 20px;
        }
        
        .scenario-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 15px;
            flex-wrap: wrap;
        }
        
        .scenario-title {
            font-size: 18px;
            font-weight: 600;
            color: #111827;
        }
        
        .insight-box {
            padding: 15px;
            border-radius: 6px;
            margin: 20px 0;
        }
        
        .insight-success {
            background-color: #d1fae5;
            border-left: 4px solid #10b981;
        }
        
        .insight-info {
            background-color: #dbeafe;
            border-left: 4px solid #3b82f6;
        }
        
        .insight-warning {
            background-color: #fef3c7;
            border-left: 4px solid #f59e0b;
        }
        
        .calculation-box {
            background-color: #eff6ff;
            padding: 15px;
            border-radius: 6px;
            margin: 15px 0;
            font-size: 14px;
        }
        
        .bounds-box {
            background-color: #f0fdf4;
            padding: 15px;
            border-radius: 6px;
            margin: 10px 0;
            font-size: 14px;
        }
        
        .chart-container {
            margin: 30px 0;
            text-align: center;
        }
        
        .chart-container img {
            max-width: 100%;
            height: auto;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .help-text {
            background-color: #f3f4f6;
            padding: 12px 15px;
            border-radius: 6px;
            font-size: 13px;
            color: #6b7280;
            margin-top: 15px;
        }
        
        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
            text-align: center;
            color: #6b7280;
            font-size: 12px;
        }
        
        @media print {
            body {
                background-color: white;
                padding: 0;
            }
            
            .container {
                box-shadow: none;
            }
            
            .section {
                page-break-inside: avoid;
            }
            
            .scenario-card {
                page-break-inside: avoid;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Projections Analysis Report</h1>
            <div class="subtitle">
                ${schoolName}<br>
                Generated: ${timestamp}
            </div>
            <div class="team-badge">${teamName}</div>
        </div>
        
        ${generateSummarySection(analysis)}
        ${generateConfidenceIntervalsSection(analysis)}
        ${generateEnrollmentHistorySection(analysis)}
        ${generateBacktestScenariosSection(analysis, chartImages)}
        ${generateKeyInsightsSection(analysis)}
        
        <div class="footer">
            <p>This report was generated by the Education Data Collective Projections Analysis Tool</p>
            <p>Data is subject to change. Please verify critical information with source data.</p>
        </div>
    </div>
</body>
</html>
  `.trim();

  // Create blob and download
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * SVG Icons
 */
const ICON_SVG = {
  summary: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>',
  confidence: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>',
  history: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>',
  backtest: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4zm2.5 2.1h-15V5h15v14.1zm0-16.1h-15c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>',
  insights: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6C7.8 12.16 7 10.63 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"/></svg>'
};

/**
 * Get MAPE color class
 */
function getMapeColorClass(mape) {
  if (mape < 10) return 'success';
  if (mape < 25) return 'warning';
  return 'error';
}

/**
 * Generate Summary Section
 */
function generateSummarySection(analysis) {
  if (!analysis || !analysis.summary) {
    return '';
  }

  const summary = analysis.summary;
  const mapeClass = getMapeColorClass(summary.overall_mape || 0);

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.summary}</div>
        <h2>Summary Metrics</h2>
      </div>
      
      <div class="metric-grid">
        <div class="metric-card">
          <div class="metric-label">Overall MAPE</div>
          <div class="metric-value ${mapeClass}">${(summary.overall_mape || 0).toFixed(1)}%</div>
          <div class="metric-sublabel">Midpoint Scenario</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Years of Data</div>
          <div class="metric-value">${summary.years_of_data || 0}</div>
          <div class="metric-sublabel">Historical enrollment</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Backtest Projections</div>
          <div class="metric-value">${summary.total_projections || 0}</div>
          <div class="metric-sublabel">Scenarios tested</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Accuracy Records</div>
          <div class="metric-value">${summary.total_accuracy_records || 0}</div>
          <div class="metric-sublabel">Comparisons made</div>
        </div>
      </div>
      
      <div class="help-text">
        <strong>MAPE (Mean Absolute Percentage Error)</strong> shows the average error as a percentage of actual enrollment. 
        Lower values indicate more accurate projections. MAPE &lt; 10% is excellent, 10-25% is good, &gt; 25% indicates volatile patterns.
      </div>
    </div>
  `;
}

/**
 * Generate Confidence Intervals Section
 */
function generateConfidenceIntervalsSection(analysis) {
  if (!analysis || !analysis.confidence_intervals) {
    return '';
  }

  const intervals = analysis.confidence_intervals;
  const hasData = Object.values(intervals).some(ci => ci && ci.sample_size >= 2);

  if (!hasData) {
    return '';
  }

  let tableRows = '';
  [1, 2, 3, 4, 5].forEach(yearsAhead => {
    const ci = intervals[yearsAhead];
    if (!ci || ci.sample_size < 2) return;
    
    const sampleBadge = ci.sample_size >= 10 
      ? `<span class="badge badge-success">${ci.sample_size} samples</span>`
      : `<span class="badge badge-warning">${ci.sample_size} samples</span>`;
    
    tableRows += `
      <tr>
        <td><strong>${yearsAhead}-Year</strong></td>
        <td style="text-align: center;">±${ci.ci_80}%</td>
        <td style="text-align: center;">±${ci.ci_85}%</td>
        <td style="text-align: center;">±${ci.ci_90}%</td>
        <td style="text-align: center;">±${ci.ci_95}%</td>
        <td style="text-align: center;">${sampleBadge}</td>
      </tr>
    `;
  });

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.confidence}</div>
        <h2>Statistical Confidence Intervals</h2>
      </div>
      
      <p style="color: #6b7280; margin-bottom: 20px;">
        Based on this school's historical projection accuracy
      </p>
      
      <table>
        <thead>
          <tr>
            <th>Projection Horizon</th>
            <th style="text-align: center;">80% Confidence</th>
            <th style="text-align: center;">85% Confidence</th>
            <th style="text-align: center;">90% Confidence</th>
            <th style="text-align: center;">95% Confidence</th>
            <th style="text-align: center;">Samples</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
      
      <div class="help-text">
        <strong>How to read confidence intervals:</strong> An 80% confidence interval of ±15% means that 80% of historical projections 
        fell within 15% of actual enrollment. Higher confidence levels (90%, 95%) provide wider intervals for more conservative planning.
        Samples &lt; 10 may have less reliable confidence estimates.
      </div>
    </div>
  `;
}

/**
 * Generate Enrollment History Section
 */
function generateEnrollmentHistorySection(analysis) {
  if (!analysis || !analysis.enrollment_history || !analysis.grades) {
    return '';
  }

  const years = Object.keys(analysis.enrollment_history).sort();
  const grades = analysis.grades;

  // Generate header cells
  let headerCells = years.map(year => `<th style="text-align: center;"><strong>${year}</strong></th>`).join('');

  // Generate grade rows
  let gradeRows = '';
  grades.forEach(gradeCode => {
    const firstYear = years[0];
    const gradeLabel = analysis.enrollment_history[firstYear]?.grades[gradeCode]?.label || gradeCode;
    
    let cells = years.map(year => {
      const gradeData = analysis.enrollment_history[year]?.grades[gradeCode];
      if (gradeData) {
        const cellClass = gradeData.enrollment > 0 ? 'enrollment-active' : 'enrollment-empty';
        return `<td style="text-align: center;"><span class="enrollment-cell ${cellClass}">${gradeData.enrollment}</span></td>`;
      }
      return `<td style="text-align: center;"><span class="enrollment-cell enrollment-empty">—</span></td>`;
    }).join('');
    
    gradeRows += `<tr><td><strong>${gradeLabel}</strong></td>${cells}</tr>`;
  });

  // Generate total row
  let totalCells = years.map(year => {
    const total = analysis.enrollment_history[year]?.total || 0;
    return `<td style="text-align: center;"><strong>${total}</strong></td>`;
  }).join('');

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.history}</div>
        <h2>Historical Enrollment by Grade</h2>
      </div>
      
      <p style="color: #6b7280; margin-bottom: 20px;">
        Grade-level enrollment shows phasing patterns, growth trajectories, and data stability
      </p>
      
      <table>
        <thead>
          <tr style="background-color: #f3f4f6;">
            <th><strong>Grade</strong></th>
            ${headerCells}
          </tr>
        </thead>
        <tbody>
          ${gradeRows}
          <tr style="background-color: #f3f4f6;">
            <td><strong>Total</strong></td>
            ${totalCells}
          </tr>
        </tbody>
      </table>
      
      <div class="help-text">
        <strong>How to read this table:</strong> Empty cells (—) indicate grades not offered that year. 
        Look for patterns like grades being added over time (phasing in) or removed (phasing out). 
        Green-highlighted cells show active enrollment.
      </div>
    </div>
  `;
}

/**
 * Generate Backtest Scenarios Section
 */
function generateBacktestScenariosSection(analysis, chartImages) {
  if (!analysis || !analysis.backtest_scenarios || analysis.backtest_scenarios.length === 0) {
    return '';
  }

  let scenarioCards = analysis.backtest_scenarios.map((scenario, index) => {
    const mapeClass = getMapeColorClass(scenario.mape || 0);
    const stabilityBadge = scenario.grade_stability === 'Stable' 
      ? '<span class="badge badge-success">Stable</span>' 
      : '<span class="badge badge-warning">Phasing In/Out</span>';

    // Generate accuracy table
    let accuracyRows = '';
    if (scenario.accuracy_by_scenario.median && scenario.accuracy_by_scenario.median.length > 0) {
      accuracyRows = scenario.accuracy_by_scenario.median.map(medianRecord => {
        const minRecord = scenario.accuracy_by_scenario.min?.find(r => r.year === medianRecord.year);
        const maxRecord = scenario.accuracy_by_scenario.max?.find(r => r.year === medianRecord.year);
        const withinBounds = minRecord && maxRecord && 
          medianRecord.actual >= minRecord.projected && 
          medianRecord.actual <= maxRecord.projected;
        
        const withinBoundsCell = minRecord && maxRecord
          ? (withinBounds 
              ? '<span class="badge badge-success">✓ Yes</span>' 
              : '<span class="badge badge-warning">✗ No</span>')
          : '—';
        
        const errorDisplay = medianRecord.percent_error !== null
          ? `${Math.abs(medianRecord.percent_error).toFixed(2)}%`
          : '—';
        
        return `
          <tr>
            <td><strong>${medianRecord.year}</strong></td>
            <td style="text-align: right; color: #4338ca;">${minRecord?.projected || '—'}</td>
            <td style="text-align: right;"><strong>${medianRecord.projected}</strong></td>
            <td style="text-align: right; color: #4338ca;">${maxRecord?.projected || '—'}</td>
            <td style="text-align: right; ${withinBounds ? 'color: #10b981;' : 'color: #f59e0b;'} font-weight: bold;">${medianRecord.actual}</td>
            <td style="text-align: center;">${withinBoundsCell}</td>
            <td style="text-align: right;">${errorDisplay}</td>
          </tr>
        `;
      }).join('');
    }

    // Calculate within bounds rate
    let withinBoundsInfo = '';
    if (scenario.accuracy_by_scenario.min?.length > 0 && scenario.accuracy_by_scenario.max?.length > 0) {
      const withinBoundsCount = scenario.accuracy_by_scenario.median.filter(medianRecord => {
        const minRecord = scenario.accuracy_by_scenario.min.find(r => r.year === medianRecord.year);
        const maxRecord = scenario.accuracy_by_scenario.max.find(r => r.year === medianRecord.year);
        return minRecord && maxRecord && medianRecord.actual >= minRecord.projected && medianRecord.actual <= maxRecord.projected;
      }).length;
      const totalCount = scenario.accuracy_by_scenario.median.length;
      const rate = totalCount > 0 ? ((withinBoundsCount / totalCount) * 100).toFixed(1) : 0;
      
      let rateComment = rate >= 80 ? 'Excellent confidence interval coverage!' 
        : rate >= 60 ? 'Good coverage, consider reviewing outliers.' 
        : 'Low coverage suggests bounds may need adjustment.';
      
      withinBoundsInfo = `
        <div class="bounds-box">
          <strong>Within Bounds Rate:</strong> ${withinBoundsCount} out of ${totalCount} projections (${rate}%) had actual enrollment within the Low/High range. ${rateComment}
        </div>
      `;
    }

    // MAPE calculation display
    let mapeCalculation = '';
    if (scenario.mape !== null && scenario.accuracy_by_scenario.median?.length > 0) {
      const errorTerms = scenario.accuracy_by_scenario.median
        .filter(r => r.percent_error !== null)
        .map(r => `${Math.abs(r.percent_error).toFixed(2)}%`)
        .join(' + ');
      const count = scenario.accuracy_by_scenario.median.filter(r => r.percent_error !== null).length;
      
      mapeCalculation = `
        <div class="calculation-box">
          <strong>MAPE Calculation (Midpoint Scenario):</strong> Average of absolute % errors = (${errorTerms}) / ${count} = <strong>${scenario.mape}%</strong>
        </div>
      `;
    }

    // Chart image if available
    const chartImg = chartImages[`scenario_${index}`] 
      ? `<div class="chart-container">
           <h3 style="color: #4338ca; margin-bottom: 15px;">Projected vs Actual Enrollment</h3>
           <img src="${chartImages[`scenario_${index}`]}" alt="Projections Chart" />
         </div>` 
      : '';

    return `
      <div class="scenario-card">
        <div class="scenario-header">
          <span class="scenario-title">Backtest Scenario #${index + 1}</span>
          ${scenario.mape !== null ? `<span class="badge badge-${mapeClass}">MAPE: ${scenario.mape}%</span>` : ''}
          ${stabilityBadge}
        </div>
        
        <div style="margin-bottom: 15px;">
          <p style="color: #6b7280; margin: 5px 0;">
            <strong>Setup:</strong> Used ${scenario.source_data_years.length} years of data (${scenario.source_data_years[0].split('-')[0]}-${scenario.source_data_years[scenario.source_data_years.length - 1].split('-')[0]})
          </p>
          <p style="color: #6b7280; margin: 5px 0;">
            <strong>Entry Grade:</strong> ${scenario.entry_grade}
          </p>
          <p style="color: #6b7280; margin: 5px 0;">
            <strong>Grade Stability:</strong> ${scenario.grade_stability === 'Stable' 
              ? 'School offered the same grades throughout this period' 
              : 'School added or removed grades during this period'}
          </p>
          <p style="color: #6b7280; margin: 5px 0;">
            <strong>Simulation:</strong> "If we made projections using only this historical data, how accurate would they be?"
          </p>
        </div>
        
        ${chartImg}
        
        <h3>Projection Results (All Scenarios)</h3>
        <table>
          <thead>
            <tr style="background-color: #f3f4f6;">
              <th>School Year</th>
              <th style="text-align: right;">Low</th>
              <th style="text-align: right;">Midpoint</th>
              <th style="text-align: right;">High</th>
              <th style="text-align: right;">Actual</th>
              <th style="text-align: center;">Within Bounds?</th>
              <th style="text-align: right;">Midpoint % Error</th>
            </tr>
          </thead>
          <tbody>
            ${accuracyRows || '<tr><td colspan="7" style="text-align: center; color: #6b7280;">No accuracy records available for this projection</td></tr>'}
          </tbody>
        </table>
        
        <div class="help-text">
          <strong>How to read this table:</strong> The <strong>Low</strong> and <strong>High</strong> columns show the projected range (min/max scenarios). 
          The <strong>Actual</strong> enrollment should ideally fall within this range. Green actual values indicate they were within bounds; 
          orange indicates they fell outside the predicted range.
        </div>
        
        ${mapeCalculation}
        ${withinBoundsInfo}
      </div>
    `;
  }).join('');

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.backtest}</div>
        <h2>Backtest Scenarios</h2>
      </div>
      
      <p style="color: #6b7280; margin-bottom: 20px;">
        Each scenario simulates what projections would have looked like using only historical data available at that time
      </p>
      
      ${scenarioCards}
    </div>
  `;
}

/**
 * Generate Key Insights Section
 */
function generateKeyInsightsSection(analysis) {
  if (!analysis || !analysis.summary) {
    return '';
  }

  const mape = analysis.summary.overall_mape || 0;
  let insightBox;

  if (mape < 10) {
    insightBox = `
      <div class="insight-box insight-success">
        <h4 style="margin-bottom: 10px; color: #065f46;">Exceptional Accuracy (MAPE &lt; 10%)</h4>
        <p style="color: #065f46;">
          This school shows very stable enrollment patterns. The projection model performs excellently, 
          with typical errors of less than ${mape.toFixed(1)}%. This level of accuracy is highly actionable for planning purposes.
        </p>
      </div>
    `;
  } else if (mape < 25) {
    insightBox = `
      <div class="insight-box insight-info">
        <h4 style="margin-bottom: 10px; color: #1e40af;">Good Accuracy (MAPE 10-25%)</h4>
        <p style="color: #1e40af;">
          The projections show reasonable accuracy with an average error of ${mape.toFixed(1)}%. 
          This is suitable for medium-term planning but should be updated regularly as new data becomes available.
        </p>
      </div>
    `;
  } else {
    insightBox = `
      <div class="insight-box insight-warning">
        <h4 style="margin-bottom: 10px; color: #92400e;">Lower Accuracy (MAPE &gt; 25%)</h4>
        <p style="color: #92400e;">
          With MAPE of ${mape.toFixed(1)}%, this school shows more volatile or unpredictable enrollment patterns. 
          Projections should be used with caution and supplemented with qualitative insights about school-specific factors.
        </p>
      </div>
    `;
  }

  return `
    <div class="section">
      <div class="section-header">
        <div class="section-icon">${ICON_SVG.insights}</div>
        <h2>Key Insights</h2>
      </div>
      
      ${insightBox}
      
      <h3 style="margin-top: 25px;">Why These Results Matter</h3>
      <ul style="padding-left: 25px; margin-top: 15px;">
        <li style="margin-bottom: 12px;">
          <strong>Backtesting validates the model:</strong> By simulating past projections and comparing to reality, 
          we can quantify exactly how accurate our predictions were.
        </li>
        <li style="margin-bottom: 12px;">
          <strong>School-specific performance:</strong> The model works better for some schools than others, 
          helping identify which projections to trust more.
        </li>
        <li style="margin-bottom: 12px;">
          <strong>Shorter timeframes are more accurate:</strong> Projections 1-2 years out are typically much more 
          reliable than 4-5 year projections.
        </li>
      </ul>
    </div>
  `;
}

export default exportProjectionsAnalysisToHTML;

